/**
 * Audit log.
 *
 * Append-only, cursor-paginated. The cursor matters: with offset pagination, an
 * event landing mid-read shifts every subsequent page and a reader silently skips a
 * row — exactly the failure an audit log must not have.
 */

import { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'

import { useAuditEvents, useExport, useOrganization } from '@/shared/api/hooks'
import { AppShell, PageHead } from '@/shared/ui/Chrome'
import {
  Badge,
  Button,
  Empty,
  ErrorNotice,
  Loading,
  Panel,
  formatDateTime,
} from '@/shared/ui/primitives'
import { NoWorkspace, useCurrentOrgId } from './Team'

/** Human labels for the audit actions the backend emits. */
const ACTION_LABELS: Record<string, string> = {
  'organization.created': 'Workspace created',
  'organization.renamed': 'Workspace renamed',
  'organization.deleted': 'Workspace deleted',
  'organization.ownership_transferred': 'Ownership transferred',
  'member.invited': 'Member invited',
  'member.joined': 'Member joined',
  'member.removed': 'Member removed',
  'member.suspended': 'Member suspended',
  'member.reactivated': 'Member reactivated',
  'member.role_changed': 'Role changed',
  'invitation.revoked': 'Invitation revoked',
  'invitation.resent': 'Invitation resent',
  'member.provisioned': 'Member provisioned (SCIM)',
  'member.linked': 'Provisioned member linked',
  'group.provisioned': 'Group provisioned (SCIM)',
  'group.changed': 'Group changed (SCIM)',
  'group.removed': 'Group removed (SCIM)',
  'provisioning.token_created': 'Provisioning token created',
  'provisioning.token_revoked': 'Provisioning token revoked',
  'organization.sso_changed': 'SSO configuration changed',
  'preindex.artifact_published': 'CI index published',
  'seat.assigned': 'Seat assigned',
  'seat.revoked': 'Seat revoked',
  'seat.quantity_changed': 'Seat count changed',
  'seat.billing_reconciled': 'Active seats billed',
  'device.activated': 'Device activated',
  'device.revoked': 'Device revoked',
  'subscription.plan_changed': 'Plan changed',
  'subscription.status_changed': 'Subscription status changed',
  'policy.changed': 'Policy changed',
}

/** Destructive and billing-relevant actions get a tone so a scan finds them. */
function toneFor(action: string): 'coral' | 'amber' | 'signal' | undefined {
  if (action.includes('revoked') || action.includes('removed') || action.includes('deleted')) {
    return 'coral'
  }
  if (action.startsWith('subscription') || action.startsWith('seat.quantity')) {
    return 'amber'
  }
  if (action.includes('joined') || action.includes('assigned') || action.includes('created')) {
    return 'signal'
  }
  return undefined
}

export function Audit() {
  const orgId = useCurrentOrgId()
  if (!orgId) return <NoWorkspace />
  return <AuditLog orgId={orgId} />
}

function AuditLog({ orgId }: { orgId: string }) {
  const { data: org } = useOrganization(orgId)
  const isFreeTeam = org?.plan === 'free_team' || !org?.subscription_status

  // A stack of cursors rather than a single one, so "previous" works without
  // refetching from the beginning.
  const [cursors, setCursors] = useState<string[]>([''])
  const cursor = cursors[cursors.length - 1] ?? ''
  const { data, isPending, isFetching, error } = useAuditEvents(orgId, cursor, {
    enabled: !isFreeTeam && org?.role !== 'member',
  })
  const exportCsv = useExport(orgId)

  if (org && org.role === 'member') {
    return <Navigate to="/team" replace />
  }

  const events = data?.events ?? []
  const nextCursor = data?.next_cursor ?? ''

  return (
    <AppShell>
      <PageHead
        index="08"
        eyebrow="Audit"
        title="Audit log"
        subtitle="Every administrative change, append-only. Nothing in this system can edit or delete a row."
        actions={
          <Button
            size="sm"
            disabled={isFreeTeam}
            loading={exportCsv.isPending}
            onClick={() => exportCsv.mutate('audit-events')}
          >
            Export CSV
          </Button>
        }
      />

      <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
        {isFreeTeam && (
          <Panel title="Included in Archis Team (3+ seats)" index="01" tone="signal">
            <div className="stack" style={{ ['--gap' as string]: 'var(--s3)' }}>
              <p className="dim" style={{ margin: 0, maxWidth: '62ch' }}>
                The append-only audit trail and compliance CSV export are included in the Archis Team plan.
                All workspace administrative events (member invitations, seat assignments, role changes, device revocations)
                are recorded in the background and will be immediately accessible upon upgrading.
              </p>
              <div className="row" style={{ gap: 'var(--s3)' }}>
                <Link to="/billing">
                  <Button variant="primary">Upgrade to Archis Team →</Button>
                </Link>
                <Link to="/pricing">
                  <Button variant="ghost">Compare plans</Button>
                </Link>
              </div>
            </div>
          </Panel>
        )}

        {!isFreeTeam && isPending && <Loading label="Loading audit events" />}
        {!isFreeTeam && <ErrorNotice error={error} />}
        <ErrorNotice error={exportCsv.error} />

        {!isFreeTeam && (
          <Panel
            title="Events"
            index="01"
            padded={false}
            actions={isFetching && !isPending ? <span className="spinner" /> : undefined}
            footer={
              <div className="row row--between">
                <span className="mono-note">
                  Page {cursors.length} · {events.length} events
                </span>
                <div className="row">
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={cursors.length === 1}
                    onClick={() => setCursors((current) => current.slice(0, -1))}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    disabled={!nextCursor}
                    onClick={() => setCursors((current) => [...current, nextCursor])}
                  >
                    Next
                  </Button>
                </div>
              </div>
            }
          >
            {!isPending && events.length === 0 && <Empty>No audit events yet.</Empty>}

            {events.length > 0 && (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Action</th>
                      <th>Actor</th>
                      <th>Target</th>
                      <th>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((event, index) => (
                      <tr key={event.id ?? event.ID ?? `${event.created_at}-${index}`}>
                        <td className="mono-note" style={{ whiteSpace: 'nowrap' }}>
                          {formatDateTime(event.created_at)}
                        </td>
                        <td>
                          <Badge tone={toneFor(event.action)} dot>
                            {ACTION_LABELS[event.action] ?? event.action}
                          </Badge>
                        </td>
                        <td>
                          {event.actor_email ? (
                            event.actor_email
                          ) : (
                            <span className="muted">
                              {event.actor_type === 'stripe' ? 'billing webhook' : 'system'}
                            </span>
                          )}
                        </td>
                        <td className="mono-note">
                          {event.target_type ? `${event.target_type}` : '—'}
                        </td>
                        <td className="mono-note">
                          {event.metadata
                            ? Object.entries(event.metadata)
                                .map(([key, value]) => `${key}=${value}`)
                                .join(' · ')
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        )}

        {!isFreeTeam && (
          <p className="mono-note">
            The CSV export includes up to 4,000 events. Need a longer history or a
            SIEM-friendly feed? Ask support.
          </p>
        )}
      </div>
    </AppShell>
  )
}
